import React from 'react';

import { useAuth } from '@/contexts';
import { Scope } from '@/types';

import { Card } from './ui';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireScope?: Scope;
}

function ProtectedRoute({ children, requireScope }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, hasScope, payload } = useAuth();
  // Show loading state while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card title="Loading">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p>Checking your access...</p>
          </div>
        </Card>
      </div>
    );
  }
  // Show access required message if not authenticated
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Card title="Access Required">
          <div className="text-center space-y-4">
            <div className="text-blue-600">
              <svg
                className="mx-auto h-12 w-12 mb-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900">Authentication Required</h3>
            <p className="text-gray-600">Please use the direct access link provided to you to access this section.</p>
            <p className="text-sm text-gray-500">If you don't have an access link, please contact your host.</p>
          </div>
        </Card>
      </div>
    );
  }
  // Check scope if required
  if (requireScope && !hasScope(requireScope)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Card title="Access Denied">
          <div className="text-center space-y-4">
            <div className="text-red-600">
              <svg
                className="mx-auto h-12 w-12 mb-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900">Insufficient Permissions</h3>
            <p className="text-gray-600">You don't have the required permissions to access this section.</p>
            <p className="text-sm text-gray-500">
              Required scope: <code className="bg-gray-100 px-2 py-1 rounded">{requireScope}</code>
            </p>
            <p className="text-sm text-gray-500">
              Your scopes: {payload?.scope.length ? payload.scope.join(', ') : 'None'}
            </p>
          </div>
        </Card>
      </div>
    );
  }
  return <>{children}</>;
}

export default ProtectedRoute;
